import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { get, useFormContext, useFormState } from 'react-hook-form'
import { UnsavedFieldsContext } from '../form-new/field-meta'
import { scrollBehavior, scrollToElement } from './section-nav'

// What is unsaved in the connection workspace, field by field (owner
// request 2026-09-28: "see what the unsaved change is ... maybe even jump to
// them"). The list is derived from react-hook-form's own dirty state -- the
// same state the save bar's count and the leave guard read -- so it can't
// drift from them: every dirty path is one change, a list field (tags) is
// one change however many entries moved.
//
// UnsavedChangesProvider (inside FormNew) collects each field's label,
// section and secrecy from FormNewLabel; a dirty path no label registered
// falls back to its name, the section found in the page, and secrecy by
// name.

const FLASH_MS = 1600
const MAX_VALUE = 48
const SECRET_NAME = /secret|private|password|pem|certificate|token/i

export const UnsavedChangesProvider = ({ children }) => {
  const [meta, setMeta] = useState({})

  const register = useCallback((name, info) => {
    setMeta(prev => {
      const old = prev[name]
      if (old && old.label === info.label && old.sectionId === info.sectionId && old.sectionTitle === info.sectionTitle && old.secret === info.secret) return prev
      return { ...prev, [name]: info }
    })
    return () => setMeta(prev => {
      if (!(name in prev)) return prev
      const next = { ...prev }
      delete next[name]
      return next
    })
  }, [])

  const value = useMemo(() => ({ meta, register }), [meta, register])

  return <UnsavedFieldsContext.Provider value={value}>{children}</UnsavedFieldsContext.Provider>
}

// Every dirty leaf path; an array (a list field) is one path.
export const dirtyPaths = (dirty, prefix = '') => {
  if (dirty === true) return prefix ? [prefix] : []
  if (!dirty || typeof dirty !== 'object') return []
  if (Array.isArray(dirty)) return dirty.some(d => countTrue(d)) && prefix ? [prefix] : []
  return Object.keys(dirty).flatMap(key => dirtyPaths(dirty[key], prefix ? `${prefix}.${key}` : key))
}

const countTrue = d => d === true || (d && typeof d === 'object' && Object.values(d).some(countTrue))

export const formatValue = value => {
  let text
  if (value === undefined || value === null || value === '') text = '(empty)'
  else if (Array.isArray(value)) text = value.length ? value.map(v => (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v))).join(', ') : '(none)'
  else if (typeof value === 'boolean') text = value ? 'on' : 'off'
  else if (typeof value === 'object') text = JSON.stringify(value)
  else text = String(value)
  text = text.replace(/\s+/g, ' ').trim()
  return text.length > MAX_VALUE ? `${text.slice(0, MAX_VALUE - 1)}…` : text
}

const humanize = name => {
  const last = String(name).split('.').pop()
  const words = last.replace(/New$/, '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const escape = value => (typeof window !== 'undefined' && window.CSS && window.CSS.escape ? window.CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&'))

// The field's control, and its field group (label + control).
export const fieldElements = name => {
  if (typeof document === 'undefined') return {}
  const group = document.querySelector(`[data-field="${escape(name)}"]`)
  const control = document.getElementById(name) ||
    document.querySelector(`[name="${escape(name)}"]`) ||
    (group && group.querySelector('input, select, textarea, button, [tabindex]'))
  return { control, group: group || (control && (control.closest('.sw-field') || control)) }
}

const sectionOf = name => {
  const { group, control } = fieldElements(name)
  const section = (group || control) && (group || control).closest('section.sw-section')
  if (!section) return {}
  const heading = section.querySelector('h2')
  return { sectionId: section.id, sectionTitle: heading ? heading.firstChild && heading.firstChild.nodeType === 3 ? heading.firstChild.textContent : heading.textContent : section.id }
}

// Scroll to the field (motion only if the person allows it), focus it and
// flash it.
export const jumpToField = name => {
  const { control, group } = fieldElements(name)
  const target = group || control
  if (!target) return false
  scrollToElement(target, { block: 'center' })
  if (control && typeof control.focus === 'function') control.focus({ preventScroll: true })
  target.classList.remove('sw-flash')
  // restart the animation if it's already flashing
  void target.offsetWidth // eslint-disable-line no-void
  target.classList.add('sw-flash')
  window.setTimeout(() => target.classList.remove('sw-flash'), FLASH_MS)
  return true
}

// The unsaved changes, in page order: [{ name, label, sectionId,
// sectionTitle, secret, before, after }].
export const useUnsavedChanges = () => {
  const context = useContext(UnsavedFieldsContext)
  const meta = (context && context.meta) || {}
  const { getValues, formState, resetField } = useFormContext()
  const { dirtyFields } = useFormState()
  const defaults = formState.defaultValues || {}

  const paths = dirtyPaths(dirtyFields)

  const changes = paths.map(name => {
    const registered = meta[name] || {}
    const found = registered.sectionId ? {} : sectionOf(name)
    const secret = Boolean(registered.secret) || SECRET_NAME.test(name)
    return {
      name,
      label: registered.label || humanize(name),
      sectionId: registered.sectionId || found.sectionId || null,
      sectionTitle: registered.sectionTitle || found.sectionTitle || 'Other',
      secret,
      before: secret ? null : formatValue(get(defaults, name)),
      after: secret ? null : formatValue(getValues(name))
    }
  })

  // Page order when the fields are on the page.
  const ordered = [...changes].sort((a, b) => {
    const x = fieldElements(a.name).group
    const y = fieldElements(b.name).group
    if (!x || !y || x === y) return 0
    return x.compareDocumentPosition(y) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
  })

  const undo = name => resetField(name)

  return { changes: ordered, count: ordered.length, undo }
}

// Unsaved changes per section: { [sectionId]: { count, first } }.
export const bySection = changes => {
  const out = {}
  changes.forEach(c => {
    if (!c.sectionId) return
    if (!out[c.sectionId]) out[c.sectionId] = { count: 0, first: c.name }
    out[c.sectionId].count += 1
  })
  return out
}

const groupBySection = changes => {
  const groups = []
  changes.forEach(c => {
    let group = groups.find(g => g.title === c.sectionTitle)
    if (!group) { group = { title: c.sectionTitle, items: [] }; groups.push(group) }
    group.items.push(c)
  })
  return groups
}

// The save bar's "N unsaved changes" as a button that opens the list: each
// change reads "Section → Field: old → new" (secrets only "changed"),
// jumps to its field, and has its own Undo. Esc closes and focus returns to
// the button.
export const UnsavedChangesButton = ({ changes, onUndo }) => {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef(null)
  const panelRef = useRef(null)
  const count = changes.length
  const text = count === 1 ? '1 unsaved change' : `${count} unsaved changes`

  const close = useCallback((restoreFocus = true) => {
    setOpen(false)
    if (restoreFocus && buttonRef.current) buttonRef.current.focus()
  }, [])

  useEffect(() => { if (count === 0 && open) setOpen(false) }, [count, open])

  useEffect(() => {
    if (!open) return undefined
    const first = panelRef.current && panelRef.current.querySelector('button')
    if (first) first.focus()
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); close() } }
    const onDown = e => {
      if (panelRef.current && !panelRef.current.contains(e.target) && buttonRef.current && !buttonRef.current.contains(e.target)) close(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown) }
  }, [open, close])

  const jump = name => { setOpen(false); jumpToField(name) }

  const undo = change => {
    onUndo(change.name)
    // keep focus in the list (or back on the button when it was the last)
    window.setTimeout(() => {
      const next = panelRef.current && panelRef.current.querySelector('button')
      if (next) next.focus()
      else if (buttonRef.current) buttonRef.current.focus()
    }, 0)
  }

  return (
    <span className="sw-unsaved">
      <button ref={buttonRef} type="button" className="btn btn-link sw-unsaved-toggle p-0" aria-haspopup="dialog" aria-expanded={open}
        aria-controls="sw-unsaved-list" disabled={count === 0} onClick={() => (open ? close() : setOpen(true))}>
        {text}
        <span aria-hidden="true" className="ms-1">{open ? '▾' : '▴'}</span>
      </button>
      {open &&
        <div ref={panelRef} id="sw-unsaved-list" className="sw-unsaved-panel" role="dialog" aria-label="Unsaved changes">
          {groupBySection(changes).map(group =>
            <div key={group.title} className="sw-unsaved-group">
              <div className="sw-unsaved-section">{group.title}</div>
              <ul>
                {group.items.map(change =>
                  <li key={change.name}>
                    <button type="button" className="sw-unsaved-item" onClick={() => jump(change.name)}>
                      <span className="sw-unsaved-where">{group.title} → {change.label}</span>
                      <span className="sw-unsaved-what">
                        {change.secret
                          ? 'changed'
                          : <>{change.before} <span aria-hidden="true">→</span><span className="visually-hidden">to</span> {change.after}</>}
                      </span>
                    </button>
                    <button type="button" className="btn sw-btn btn-sm" aria-label={`Undo ${change.label}`} onClick={() => undo(change)}>Undo</button>
                  </li>)}
              </ul>
            </div>)}
        </div>}
    </span>
  )
}

export { scrollBehavior }
